/**
 * Pause / settings + photo share + THPS-style character select.
 */
import { V, St, xt, bt, wt } from "./dom.js";
import { displayName, thumbUrl } from "../characters/roster.js";

/**
 * Settings / pause menu.
 * @param {{ onResume: () => void, onChangeCharacter: () => void }} opts
 */
export function Ot(opts) {
  const onResume = typeof opts === "function" ? opts : opts.onResume;
  const onChangeCharacter =
    typeof opts === "function" ? null : opts.onChangeCharacter;
  const resume = St(`Resume`, `play`, onResume);
  const items = [resume];
  if (onChangeCharacter) {
    items.push(St(`Change character`, `person`, onChangeCharacter));
  }
  return {
    el: V(
      `div`,
      { class: `screen pause` },
      V(`div`, { class: `title` }, `paused`),
      V(`div`, { class: `pause-items` }, ...items),
    ),
    onBack: onResume,
    initialFocus: () => resume,
  };
}

export function kt(e) {
  let t = V(
      `span`,
      null,
      e.online
        ? `Share it to the game's Wavedash page.`
        : `Save it to your device.`,
    ),
    n = xt(
      `Share to Wavedash`,
      async () => {
        n.disabled = !0;
        t.textContent = `Uploading…`;
        let r = await e.share(e.png);
        t.textContent = r
          ? `Shared. It's on the game's page now.`
          : `Upload failed. You can still save it.`;
        n.disabled = r;
      },
      { primary: !0, icon: `share` },
    ),
    r = xt(
      `Save`,
      () =>
        V(`a`, {
          href: e.dataUrl,
          download: `afters-${Date.now()}.png`,
        }).click(),
      { icon: `download` },
    ),
    i = [
      ...(e.online ? [n] : []),
      r,
      xt(`Close`, e.close, { icon: `close` }),
    ];
  return {
    el: V(
      `div`,
      { class: `screen photo` },
      wt({
        icon: `camera`,
        title: `Snapshot`,
        body: V(`img`, {
          class: `snapshot`,
          src: e.dataUrl,
          alt: `Snapshot of the afters`,
        }),
        foot: [t, V(`div`, { class: `btn-row` }, i)],
      }),
    ),
    onBack: e.close,
    initialFocus: () => i[0],
  };
}

/**
 * THPS-style character carousel with 3D dancing preview + side arrows / swipe.
 * @param {{
 *   roster: { name: string, sex: string }[],
 *   currentName?: string | null,
 *   onPick: (entry: { name: string, sex: string }) => void | Promise<void>,
 *   onBack: () => void,
 *   mountPreview?: (canvas: HTMLCanvasElement) => { show(meta): Promise<void>, dispose(): void },
 * }} opts
 */
export function characterSelect(opts) {
  const roster = opts.roster || [];
  let index = Math.max(
    0,
    roster.findIndex((c) => c.name === opts.currentName),
  );
  if (index < 0) index = 0;

  const title = V(`div`, { class: `char-name` }, ``);
  const meta = V(`div`, { class: `char-meta` }, ``);
  const canvas = V(`canvas`, {
    class: `char-hero-canvas`,
    "aria-label": `Character preview`,
  });
  const strip = V(`div`, { class: `char-strip` });
  const status = V(`div`, { class: `char-status` }, ``);

  const arrowPrev = V(
    `button`,
    {
      class: `char-arrow char-arrow-prev interactive`,
      type: `button`,
      "aria-label": `Previous character`,
      "data-nav": !0,
    },
    bt(`back`),
  );
  const arrowNext = V(
    `button`,
    {
      class: `char-arrow char-arrow-next interactive`,
      type: `button`,
      "aria-label": `Next character`,
      "data-nav": !0,
    },
    bt(`back`),
  );
  arrowPrev.addEventListener(`click`, (e) => {
    e.preventDefault();
    e.stopPropagation();
    step(-1);
  });
  arrowNext.addEventListener(`click`, (e) => {
    e.preventDefault();
    e.stopPropagation();
    step(1);
  });

  const select = xt(
    `Select`,
    async () => {
      const entry = roster[index];
      if (!entry || select.disabled) return;
      select.disabled = !0;
      status.textContent = `Loading…`;
      try {
        await opts.onPick(entry);
      } catch (err) {
        console.error(err);
        status.textContent = `Couldn't load that character.`;
        select.disabled = !1;
      }
    },
    { primary: !0, icon: `person` },
  );
  const back = xt(`Back`, () => {
    disposePreview();
    opts.onBack();
  }, { icon: `close` });

  let preview = null;
  let previewToken = 0;

  function disposePreview() {
    try {
      preview?.dispose?.();
    } catch {}
    preview = null;
  }

  function step(delta) {
    if (!roster.length) return;
    index = (index + delta + roster.length) % roster.length;
    render();
  }

  function render() {
    const entry = roster[index];
    if (!entry) {
      title.textContent = `No characters`;
      return;
    }
    title.textContent = displayName(entry.name);
    meta.textContent = `${entry.sex === `f` ? `Female` : `Male`} · ${index + 1} / ${roster.length}`;
    status.textContent =
      entry.name === opts.currentName
        ? `Current`
        : preview
          ? `Loading…`
          : `Loading preview…`;

    const windowSize = 7;
    const half = (windowSize - 1) >> 1;
    strip.replaceChildren();
    for (let i = -half; i <= half; i++) {
      const ri = (index + i + roster.length * 10) % roster.length;
      const c = roster[ri];
      const btn = V(
        `button`,
        {
          class: `char-thumb${i === 0 ? ` is-current` : ``}`,
          type: `button`,
          "aria-label": displayName(c.name),
        },
        V(`img`, {
          src: thumbUrl(c.name),
          alt: ``,
          draggable: `false`,
        }),
      );
      btn.addEventListener(`click`, () => {
        index = ri;
        render();
      });
      strip.append(btn);
    }

    const token = ++previewToken;
    const shown = preview?.show?.(entry);
    if (shown && typeof shown.then === `function`) {
      shown.then(
        () => {
          if (token !== previewToken) return;
          status.textContent =
            entry.name === opts.currentName ? `Current` : ``;
        },
        (err) => {
          if (token !== previewToken) return;
          console.warn(`[afters] preview show failed`, err);
          status.textContent = `Preview failed`;
        },
      );
    } else if (!preview) {
      status.textContent = `No preview`;
    }
  }

  const stage = V(
    `div`,
    { class: `char-stage` },
    arrowPrev,
    V(`div`, { class: `char-viewport` }, canvas),
    arrowNext,
  );

  // Swipe on the stage / viewport
  let swipeX = null;
  let swipeY = null;
  const onPointerDown = (e) => {
    if (e.target.closest?.(`.char-arrow, .btn, .char-thumb`)) return;
    swipeX = e.clientX;
    swipeY = e.clientY;
  };
  const onPointerUp = (e) => {
    if (swipeX == null) return;
    const dx = e.clientX - swipeX;
    const dy = e.clientY - swipeY;
    swipeX = swipeY = null;
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    step(dx < 0 ? 1 : -1);
  };
  stage.addEventListener(`pointerdown`, onPointerDown);
  stage.addEventListener(`pointerup`, onPointerUp);
  stage.addEventListener(`pointercancel`, () => {
    swipeX = swipeY = null;
  });

  const el = V(
    `div`,
    { class: `screen character` },
    V(
      `div`,
      { class: `char-panel panel` },
      V(`div`, { class: `char-head` }, title, meta),
      stage,
      strip,
      status,
      V(`div`, { class: `btn-row` }, select, back),
    ),
  );

  el.tabIndex = -1;
  el.addEventListener(`keydown`, (e) => {
    if (e.code === `ArrowLeft`) {
      e.preventDefault();
      step(-1);
    } else if (e.code === `ArrowRight`) {
      e.preventDefault();
      step(1);
    }
  });

  // Mount 3D preview after the canvas is in the DOM
  requestAnimationFrame(() => {
    if (typeof opts.mountPreview === `function`) {
      try {
        preview = opts.mountPreview(canvas);
      } catch (err) {
        console.warn(`[afters] preview mount failed`, err);
      }
    }
    render();
  });

  return {
    el,
    onBack: () => {
      disposePreview();
      opts.onBack();
    },
    dispose: disposePreview,
    initialFocus: () => select,
    onStep: (delta) => {
      step(delta);
      return !0;
    },
  };
}
